import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MangaCard from '@/components/feature/MangaCard';
import { mangas } from '@/mocks/manga';

export default function MangaDetail() {
  const { id } = useParams<{ id: string }>();
  const [showAllChapters, setShowAllChapters] = useState(false);

  const manga = mangas.find((m) => m.id === id);

  const relatedMangas = useMemo(() => {
    if (!manga) return [];
    return mangas
      .filter((m) => m.id !== manga.id)
      .filter((m) => m.categories.some((c) => manga.categories.includes(c)))
      .slice(0, 5);
  }, [manga]);

  if (!manga) {
    return (
      <div className="px-4 md:px-6 pt-6">
        <div className="max-w-7xl mx-auto text-center py-20">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100 text-foreground-400">
            <i className="ri-error-warning-line text-2xl"></i>
          </div>
          <h1 className="text-xl font-bold font-heading text-foreground-900 mb-2">漫画未找到</h1>
          <p className="text-sm text-foreground-500 mb-6">您要查看的漫画不存在或已下架</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const displayedChapters = showAllChapters ? manga.chapters : manga.chapters.slice(0, 20);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${manga.title} - 汗汗漫画`,
    description: manga.description,
    author: { '@type': 'Person', name: manga.author },
    datePublished: '2024-01-01',
    dateModified: manga.updateDate,
    image: manga.coverImage,
    publisher: { '@type': 'Organization', name: '汗汗漫画' },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `${manga.title}好看吗？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${manga.title}是一部备受好评的韩国漫画，评分为${manga.rating}分，已有${(manga.views / 10000).toFixed(0)}万人阅读，是汗汗漫画平台的热门韩漫之一。`,
        },
      },
      {
        '@type': 'Question',
        name: `${manga.title}在哪里可以免费阅读？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `您可以在汗汗漫画（www.hanhanmanga.com）免费在线阅读${manga.title}的全部章节。汗汗漫画是专业的免费韩国漫画平台。`,
        },
      },
    ],
  };

  return (
    <>
      <SEO
        title={`${manga.title} - 免费韩国漫画在线阅读`}
        description={`${manga.title}：${manga.description} 作者：${manga.author}，状态：${manga.status}。在汗汗漫画免费在线阅读${manga.title}全部章节，流畅高清阅读体验，每日更新。`}
        keywords={`${manga.title},${manga.title}漫画,韩国漫画${manga.title},免费${manga.title},${manga.tags.join(',')},汗汗漫画`}
        canonicalPath={`/manga/${manga.id}`}
        ogType="article"
        ogImage={manga.coverImage}
        schema={{ ...articleSchema, ...faqSchema }}
      />
      <div className="px-4 md:px-6 pt-6">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '漫画分类', href: '/category/all' },
              { label: manga.title },
            ]}
          />

          <div className="flex flex-col md:flex-row gap-6 md:gap-8 mb-10">
            <div className="flex-shrink-0">
              <div className="w-48 md:w-56 rounded-lg overflow-hidden">
                <img
                  src={manga.coverImage}
                  alt={manga.title}
                  title={`${manga.title} - 汗汗漫画免费韩国漫画在线阅读`}
                  className="w-full aspect-[3/4] object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1">
              <h1 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-3">
                {manga.title}
              </h1>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                {manga.description}
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
                <div className="p-3 rounded-lg bg-background-100">
                  <span className="text-xs text-foreground-400">作者</span>
                  <p className="text-sm font-medium text-foreground-800">{manga.author}</p>
                </div>
                <div className="p-3 rounded-lg bg-background-100">
                  <span className="text-xs text-foreground-400">状态</span>
                  <p className={`text-sm font-medium ${manga.status === '连载中' ? 'text-primary-500' : 'text-secondary-600'}`}>
                    {manga.status}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-background-100">
                  <span className="text-xs text-foreground-400">评分</span>
                  <p className="text-sm font-medium text-accent-600 flex items-center gap-1">
                    <i className="ri-star-fill text-xs"></i>
                    {manga.rating}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-background-100">
                  <span className="text-xs text-foreground-400">阅读量</span>
                  <p className="text-sm font-medium text-foreground-800">
                    {(manga.views / 10000).toFixed(0)}万
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-background-100">
                  <span className="text-xs text-foreground-400">总章节</span>
                  <p className="text-sm font-medium text-foreground-800">{manga.chapters.length}话</p>
                </div>
                <div className="p-3 rounded-lg bg-background-100">
                  <span className="text-xs text-foreground-400">最近更新</span>
                  <p className="text-sm font-medium text-foreground-800">{manga.updateDate}</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-5">
                {manga.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 text-xs rounded-full bg-accent-100 text-accent-800 whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>

              <Link
                to={`/manga/${manga.id}`}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-play-circle-line"></i>
                开始阅读第一话
              </Link>
            </div>
          </div>

          <section className="mb-10">
            <h2 className="text-lg md:text-xl font-bold font-heading text-foreground-900 mb-4">
              章节列表
              <span className="text-sm font-normal text-foreground-500 ml-2">
                (共{manga.chapters.length}话)
              </span>
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
              {displayedChapters.map((ch) => (
                <a
                  key={ch.id}
                  href="#"
                  className="flex items-center justify-between p-2.5 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/50 transition-all duration-200 cursor-pointer"
                  onClick={(e) => e.preventDefault()}
                >
                  <span className="text-sm text-foreground-700 whitespace-nowrap">{ch.title}</span>
                  <span className="text-xs text-foreground-400 whitespace-nowrap">{ch.updateDate}</span>
                </a>
              ))}
            </div>
            {manga.chapters.length > 20 && (
              <button
                onClick={() => setShowAllChapters(!showAllChapters)}
                className="mt-4 mx-auto block px-6 py-2 text-sm text-primary-500 border border-primary-300 rounded-full hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                {showAllChapters ? '收起章节' : `查看全部${manga.chapters.length}话`}
              </button>
            )}
          </section>

          {relatedMangas.length > 0 && (
            <section className="pb-12">
              <h2 className="text-lg md:text-xl font-bold font-heading text-foreground-900 mb-4">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
                {relatedMangas.map((m) => (
                  <MangaCard key={m.id} manga={m} />
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </>
  );
}